import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

import BackButton from '../components/BackButton';
import { Shield, Users, FileText, CheckCircle, Building2, HelpCircle, Download } from 'lucide-react';

export default function PresentationRCDS() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Hero Section avec Image */}
      <div className="relative h-[400px] bg-gradient-to-r from-blue-900 to-blue-700 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1622611935038-1c4caa0db5d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzJTIwc29saWRhcml0eXxlbnwwfHx8fDE3NjQ0ODU0NzR8MA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
          <BackButton to="/" label="Retour" className="mb-4 text-white" />
          <div className="max-w-3xl">
            <Badge variant="blue" className="mb-4 px-4 py-1.5 text-sm font-semibold shadow-lg">
              Accompagnement coopératif des professionnels du bâtiment
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              BATICO — RCDS
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-6">
              Responsabilité Civile & Décennale Solidaire
            </p>
            <p className="text-lg text-blue-50 max-w-2xl">
              Un dispositif coopératif exceptionnel pour accompagner les professionnels du bâtiment 
              face aux dommages relevant de la responsabilité décennale.
            </p>
          </div>
        </div>
      </div>

      {/* Contenu Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Présentation Générale */}
        <Card className="mb-8 border-l-4 border-l-blue-600">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="h-8 w-8 text-blue-600" />
              <CardTitle className="text-3xl text-blue-900">Présentation Générale</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-slate-700 leading-relaxed">
            <p className="mb-4">
              <strong>BATICO — RCDS</strong> est un dispositif d’accompagnement coopératif destiné
              à accompagner les professionnels du bâtiment confrontés à un dommage relevant de la <strong>responsabilité décennale</strong>. 
              Les <strong>articles 1792 et 1792-2 du Code civil</strong> précisent les responsabilités
              relatives à la solidité et à l’usage de l’ouvrage ainsi qu’aux éléments d’équipement indissociables.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <p className="text-blue-900 font-medium">
                L’adhésion au dispositif ouvre la possibilité de présenter une <strong>demande de soutien</strong>.
                La demande est examinée selon les règles du dispositif ; les modalités du soutien sont
                définies par l’instance compétente, dans la limite des ressources disponibles.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Ce que couvre la Garantie */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <CardTitle className="text-3xl text-blue-900">Les formes d’accompagnement BATICO — RCDS</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Image Illustration */}
              <div className="order-2 md:order-1">
                <img
                  src="https://images.unsplash.com/photo-1667893185343-9e869ae6e1bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxidWlsZGluZyUyMHJlcGFpciUyMHByb3RlY3Rpb258ZW58MHx8fHwxNzY0NDg1NDgwfDA&ixlib=rb-4.1.0&q=85"
                  alt="Travaux de réparation"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
              </div>

              {/* Liste des couvertures */}
              <div className="order-1 md:order-2 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg mt-1">
                    <Shield className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">
                      Accompagnement dans le cadre civil de la responsabilité décennale
                    </h4>
                    <p className="text-sm text-slate-600">
                      Analyse des dommages signalés, de leurs conséquences sur l’ouvrage et des éléments
                      utiles à l’examen de la demande.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-green-100 p-2 rounded-lg mt-1">
                    <Building2 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">
                      Participation aux travaux de réparation
                    </h4>
                    <p className="text-sm text-slate-600">
                      Étude des besoins de réparation et des interventions préparatoires ou complémentaires,
                      selon le dossier et les modalités de soutien retenues.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-purple-100 p-2 rounded-lg mt-1">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">
                      Soutien coopératif exceptionnel
                    </h4>
                    <p className="text-sm text-slate-600">
                      Médiation, expertise préventive,
                      accompagnement humain et administratif, appui technique pendant les travaux de reprise.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* À qui s'adresse la RCDS */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="border-l-4 border-l-purple-500">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Users className="h-7 w-7 text-purple-600" />
                <CardTitle className="text-2xl text-blue-900">À qui s'adresse la RCDS ?</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <img
                  src="https://images.unsplash.com/photo-1760009436767-d154e930e55c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjBwcm9mZXNzaW9uYWxzfGVufDB8fHx8MTc2NDQ4NTQ4Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Professionnels du bâtiment"
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
              </div>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Artisans</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Micro-entrepreneurs</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>TPE du bâtiment</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Membres coopérateurs engagés dans une démarche solidaire et préventive</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Comment activer la garantie */}
          <Card className="border-l-4 border-l-green-500">
            <CardHeader>
              <div className="flex items-center gap-3">
                <HelpCircle className="h-7 w-7 text-green-600" />
                <CardTitle className="text-2xl text-blue-900">Comment présenter une demande ?</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <img
                  src="https://images.pexels.com/photos/7937324/pexels-photo-7937324.jpeg"
                  alt="Accompagnement et support"
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
              </div>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="bg-blue-700 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <div>
                    <strong className="text-slate-900">Déclaration du dommage</strong>
                    <p className="text-sm text-slate-600">par l'adhérent</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-blue-700 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <div>
                    <strong className="text-slate-900">Analyse préventive</strong>
                    <p className="text-sm text-slate-600">par la cellule d'expertise</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-blue-700 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <div>
                    <strong className="text-slate-900">Examen et décision</strong>
                    <p className="text-sm text-slate-600">selon les règles internes</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-blue-700 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                    4
                  </div>
                  <div>
                    <strong className="text-slate-900">Mise en œuvre des suites retenues</strong>
                    <p className="text-sm text-slate-600">selon la décision et les modalités du dispositif</p>
                  </div>
                </li>
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Base Juridique */}
        <Card className="mb-8 bg-gradient-to-r from-slate-50 to-blue-50">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-8 w-8 text-blue-600" />
              <CardTitle className="text-3xl text-blue-900">Repères sur la responsabilité des constructeurs</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white p-5 rounded-lg border-l-4 border-blue-600 shadow-sm">
              <div className="flex items-start gap-3">
                <Badge className="bg-blue-600 text-white font-semibold">Article 1792</Badge>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Article 1792 du Code civil</h4>
                  <p className="text-slate-700">
                    Responsabilité décennale du constructeur pour les dommages compromettant la solidité 
                    de l'ouvrage ou le rendant impropre à sa destination.
                  </p>
                  <a href="https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006443502" className="text-blue-700 underline underline-offset-4 text-sm">Lire l’article 1792 sur Légifrance</a>
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg border-l-4 border-purple-600 shadow-sm">
              <div className="flex items-start gap-3">
                <Badge className="bg-purple-600 text-white font-semibold">Article 1792-2</Badge>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Article 1792-2 du Code civil</h4>
                  <p className="text-slate-700">
                    Élargissement aux éléments d'équipement indissociables de l'ouvrage.
                  </p>
                  <a href="https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000006443524" className="text-blue-700 underline underline-offset-4 text-sm">Lire l’article 1792-2 sur Légifrance</a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents à télécharger */}
        <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-3">
              <Download className="h-7 w-7" />
              Documents du dispositif
            </CardTitle>
            <CardDescription className="text-blue-100">
              Téléchargez les documents complets pour en savoir plus
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <a 
                href="https://customer-assets.emergentagent.com/job_ged-messaging/artifacts/fbxz4jyx_Fiche_Produit_RCDS.docx"
                download
                className="flex items-center gap-3 bg-white/10 hover:bg-white/20 p-4 rounded-lg transition"
              >
                <FileText className="h-8 w-8" />
                <div>
                  <p className="font-semibold">Fiche Produit RCDS</p>
                  <p className="text-sm text-blue-100">Document complet (DOCX)</p>
                </div>
              </a>

              <a 
                href="/contact"
                className="flex items-center gap-3 bg-white/10 hover:bg-white/20 p-4 rounded-lg transition"
              >
                <FileText className="h-8 w-8" />
                <div>
                  <p className="font-semibold">Conditions du dispositif</p>
                  <p className="text-sm text-blue-100">Demander les modalités applicables à votre projet</p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Note légale */}
        <div className="mt-8 bg-slate-100 p-6 rounded-lg">
          <div className="flex items-start gap-3">
            <FileText className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-slate-900 mb-2">Préparer votre demande</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Consultez les conditions du dispositif et{' '}
                <a href="/contact" className="text-blue-700 underline underline-offset-4">contactez l’équipe</a>{' '}
                pour préciser les pièces nécessaires, les critères d’examen et les modalités de soutien.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
